import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle2 } from "lucide-react";

const resolveBackendUrl = () => {
  const prod = process.env.REACT_APP_BACKEND_URL;
  const local = process.env.REACT_APP_BACKEND_URL_LOCAL;
  const forceLocal = process.env.REACT_APP_USE_LOCAL_BACKEND === "true";
  const isDev = process.env.NODE_ENV === "development";
  return (forceLocal || isDev) && local ? local : prod;
};

const pub = axios.create({ baseURL: `${resolveBackendUrl()}/api` });

const RatingStars = ({ value, onChange, size = 28 }) => (
  <div className="flex gap-1 justify-center">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        className="transition-transform hover:scale-110 active:scale-95"
        data-testid={`star-${i}`}
        aria-label={`${i} stars`}
      >
        <Star
          size={size}
          className={
            i <= value ? "text-[#E9A23B] fill-[#E9A23B]" : "text-[#EBE8E2]"
          }
        />
      </button>
    ))}
  </div>
);

export default function PublicFeedback() {
  const { outletId } = useParams();
  const [sp] = useSearchParams();
  const orderId = sp.get("order") || "";

  const [outlet, setOutlet] = useState(null);
  const [rating, setRating] = useState(0);
  const [food, setFood] = useState(0);
  const [service, setService] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!outletId) return;
    pub
      .get(`/public/outlet/${outletId}`)
      .then((r) => setOutlet(r.data))
      .catch(() => setOutlet(null));
  }, [outletId]);

  const submit = async () => {
    if (rating < 1) {
      alert("Please give an overall rating");
      return;
    }
    setSubmitting(true);
    try {
      await pub.post("/public/feedback", {
        outlet_id: outletId,
        order_id: orderId || null,
        rating,
        food_rating: food || null,
        service_rating: service || null,
        name,
        phone,
        comments,
      });
      setDone(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not submit feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FBFAF8] p-6"
        data-testid="feedback-success"
      >
        <div className="bg-white border border-[#EBE8E2] rounded-lg p-8 text-center max-w-sm w-full">
          <CheckCircle2 size={56} className="mx-auto text-[#6A7F66] mb-3" />
          <div className="font-heading font-extrabold text-2xl text-[#2D2C2A]">
            Thank you!
          </div>
          <p className="text-sm text-[#7A7873] mt-2">
            Your feedback helps us serve you better.
          </p>
          {rating >= 4 && (
            <p className="text-sm text-[#D95D39] mt-3">
              ⭐ We hope to see you again soon!
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#FBFAF8] py-8 px-4"
      data-testid="public-feedback-page"
    >
      <div className="max-w-md mx-auto bg-white border border-[#EBE8E2] rounded-lg p-6 shadow-sm">
        <div className="text-center border-b border-[#EBE8E2] pb-4 mb-4">
          <div className="font-heading font-extrabold text-xl text-[#2D2C2A]">
            {outlet?.name || "Share Your Feedback"}
          </div>
          <p className="text-xs text-[#7A7873] mt-1">
            We'd love to hear about your visit.
          </p>
        </div>

        <div className="space-y-5">
          <div>
            <div className="text-sm font-semibold text-[#2D2C2A] text-center mb-2">
              Overall experience
            </div>
            <RatingStars value={rating} onChange={setRating} size={36} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-[#7A7873] text-center mb-1">
                Food
              </div>
              <RatingStars value={food} onChange={setFood} size={22} />
            </div>
            <div>
              <div className="text-xs text-[#7A7873] text-center mb-1">
                Service
              </div>
              <RatingStars value={service} onChange={setService} size={22} />
            </div>
          </div>

          <div className="space-y-2 pt-2">
            <Input
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="fb-name"
            />
            <Input
              placeholder="Phone (optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="fb-phone"
            />
            <Textarea
              placeholder="Tell us what you loved or what we can improve…"
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              data-testid="fb-comments"
            />
          </div>

          <Button
            className="w-full h-11 bg-[#D95D39] hover:bg-[#C24F2C] text-white font-semibold"
            onClick={submit}
            disabled={submitting || rating < 1}
            data-testid="submit-feedback"
          >
            {submitting ? "Sending…" : "Submit Feedback"}
          </Button>
          <p className="text-[10px] text-[#7A7873] text-center">
            Your details stay with the restaurant. No spam.
          </p>
        </div>
      </div>
    </div>
  );
}
